/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';

import {
    currentScopeInCreationVar,
    currentAdminGroupStateVar,
    currentTemplateVar,
    TemplateScope,
    modalRefVar,
} from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import {
    LogCategory,
    PanelRegion,
    PanelType,
    ScenarioName,
    StatusName,
    StepName,
    SubWorkLoad,
} from '../../../../../logger';
import {
    CheckAdminGroupExistingClient,
    CheckAdminGroupStateClient,
    AdminGroupState,
} from '../../../../../service/gen/templates';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { PreSetupTemplateStepProps, PreSetupTemplateSteps } from '../PreSetupTemplateSteps';
import { ViewScopeComponent, ViewScopeComponentProps } from './ViewScope.component';

export const ViewScopeContainer: React.FC<PreSetupTemplateStepProps> = (
    props: PreSetupTemplateStepProps
): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const isPreviewTemplateEnabled = host.getEcsConfig().MicrosoftTeamApprovals.TemplatePreviewEnabled;
    const restoreGroupEnabled = host.getEcsConfig().MicrosoftTeamApprovals.RestoreGroupEnabled;

    const { navigateTo, done } = props;

    const [showTeamScope, setShowTeamScope] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const currentAdminGroupState = useReactiveVar(currentAdminGroupStateVar);
    const currentTemplate = useReactiveVar(currentTemplateVar);
    const [showError, setShowError] = React.useState(false);

    React.useEffect((): void => {
        modalRefVar()?.current?.focus();
    }, []);

    React.useEffect((): void => {
        const controlStates = async (): Promise<void> => {
            setShowTeamScope(await host.getIsTeamOwner());
        };

        controlStates();
    }, [host, setShowTeamScope, setIsLoading]);

    const goToNextStep = (currentAdminGroupState: AdminGroupState): void => {
        if (currentAdminGroupState === AdminGroupState.Exist) {
            done();
        } else {
            navigateTo(PreSetupTemplateSteps.CreateAdminGroup);
        }
    };

    const onScopeSelected = (scope: TemplateScope): void => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.SelectScope, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.SelectScope,
            'click',
            'nav',
            'templateScopeInCreation',
            'templateScopeInCreationButton',
            PanelRegion.Modal,
            PanelType.PreSetupTemplateInstance,
            {
                scope,
            }
        );

        currentScopeInCreationVar(scope);

        if (scope === TemplateScope.TenantAll || scope === TemplateScope.TenantSpecificPeople) {
            logger.logTraceInfo(LogCategory.PreSetupInstanceWizard, 'Checking if admin team exists.');
            if (currentAdminGroupState === AdminGroupState.Unknown) {
                const startTime = performance.now();
                setIsLoading(true);
                if (!restoreGroupEnabled) {
                    new CheckAdminGroupExistingClient()
                        .checkAdminGroupExisting()
                        .then((result: boolean): void => {
                            logger.logTraceInfo(
                                LogCategory.PreSetupInstanceWizard,
                                `CheckAdminGroupExisting result is: ${result}`
                            );
                            logger.markScenario(
                                ScenarioName.CreateTemplateInstance,
                                StepName.SelectScope,
                                StatusName.Success,
                                {
                                    stepElapsed: performance.now() - startTime,
                                    data: {
                                        isAdminGroupExisting: result,
                                    },
                                }
                            );
                            if (result) {
                                currentAdminGroupStateVar(AdminGroupState.Exist);
                                goToNextStep(AdminGroupState.Exist);
                            } else {
                                currentAdminGroupStateVar(AdminGroupState.NotCreated);
                                goToNextStep(AdminGroupState.NotCreated);
                            }
                        })
                        .catch((error: any): void => {
                            logger.logTraceHttpException('CheckAdminGroupExisting', error.message, error.status);
                            logger.markScenario(
                                ScenarioName.CreateTemplateInstance,
                                StepName.SelectScope,
                                StatusName.Fail,
                                {
                                    stepElapsed: performance.now() - startTime,
                                }
                            );

                            setShowError(true);
                        })
                        .finally((): void => {
                            setIsLoading(false);
                        });
                } else {
                    new CheckAdminGroupStateClient()
                        .checkAdminGroupExisting()
                        .then((result: AdminGroupState): void => {
                            logger.logTraceInfo(
                                LogCategory.PreSetupInstanceWizard,
                                `CheckAdminGroupState result is: ${result}`
                            );
                            logger.markScenario(
                                ScenarioName.CreateTemplateInstance,
                                StepName.SelectScope,
                                StatusName.Success,
                                {
                                    stepElapsed: performance.now() - startTime,
                                    data: {
                                        isAdminGroupExisting: result,
                                    },
                                }
                            );
                            currentAdminGroupStateVar(result);
                            goToNextStep(result);
                        })
                        .catch((error: any): void => {
                            logger.logTraceHttpException('CheckAdminGroupState', error.message, error.status);
                            logger.markScenario(
                                ScenarioName.CreateTemplateInstance,
                                StepName.SelectScope,
                                StatusName.Fail,
                                {
                                    stepElapsed: performance.now() - startTime,
                                }
                            );

                            setShowError(true);
                        })
                        .finally((): void => {
                            setIsLoading(false);
                        });
                }
            } else {
                goToNextStep(currentAdminGroupState);
            }
        } else {
            navigateTo(PreSetupTemplateSteps.SelectTeam);
        }
    };

    const onBack = (): void => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.CarouselPreviousClicked, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.CarouselPreviousClicked,
            'click',
            'nav',
            'ActionButton',
            'carouselButton',
            PanelRegion.Modal,
            PanelType.PreSetupTemplateInstance
        );

        if (currentTemplate) {
            if (isPreviewTemplateEnabled) {
                navigateTo(PreSetupTemplateSteps.PreviewTemplate);
            } else {
                navigateTo(PreSetupTemplateSteps.BrowseTemplates);
            }
        } else {
            navigateTo(PreSetupTemplateSteps.BrowseTemplates);
        }
    };

    const componentProps: ViewScopeComponentProps = {
        onScopeSelected,
        onBack,
        showTeamScope,
        isLoading,
        showError,
        setShowError,
    };

    return <ViewScopeComponent {...componentProps} />;
};
